import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, query } from 'lit/decorators.js';
import componentStyles from './se-main-header.css.lit';
import { MobileController } from '../../controllers/mobile-controller';

import '../se-container/se-container';
/**
 * The SeMainHeader component
 * @element se-main-header
 */
@customElement('se-main-header')
export class SeMainHeader extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  mobileController = new MobileController(this);
  resizeObserver: ResizeObserver;

  @property({ type: Boolean })
  hasOverlay: boolean;

  @property({ type: Boolean })
  searchOpen = false;

  @property({ type: String, attribute: 'contact-link' })
  contactLink: string;

  @property({ type: Boolean })
  mobileMenuOpen = false;

  @property({ type: Boolean })
  isMobile: boolean;

  @query('.se-main-header--nav-utility')
  mobileMenu: HTMLElement;

  @query('.se-main-header--search-slot')
  searchPanel: HTMLElement;

  @query('.se-main-header')
  main: HTMLElement;

  render(): TemplateResult {
    return html`
      <header class="se-main-header--wrapper">
        <div class="se-main-header">
          ${this.mobileController.isMobile
            ? this.mobileTemplate()
            : this.desktopTemplate()}
        </div>
      </header>
    `;
  }

  desktopTemplate() {
    return html`
          <div class="se-main-header--main">
            <slot name="utility"></slot>
            <div class="se-main-header--top-content">
              <se-container>
                <div class="se-main-header--logo">
                  <slot name="logo"></slot>
                </div>
                <div class="se-main-header--nav-search">
                  <div class="se-main-header--nav">
                    <slot class="se-main-header--nav-slot" name="nav"></slot>
                  </div>
                  <div class="se-main-header--search">
                    <se-button 
                    variant="primary-2"
                    button-label="contact us"
                    button-url="${this.contactLink}"
                    size="slim"
                    >Contact Us
                    </se-button>
                    <se-button
                        id="search-nav-button"
                        variant="none"
                        ?is-disabled=${this.searchOpen ? true : false}
                        @click=${this.toggleSearch}
                        button-label="search"
                        aria-controls="mobile-search-panel"
                        aria-haspopup="true"
                        >
                        <outline-icon
                        slot="left"
                        library="system"
                        name="search"
                        size="22px"
                        ></outline-icon>
                      </se-button>
                    </div>
                  </div>
                </se-container>
                </div>
              </div>
            </div>
            <div class="se-main-header--search-slot closed"  id="mobile-search-panel" aria-expanded="${
              this.searchOpen
            }">
              <slot name="search"></slot>
            </div>
            `;
  }

  mobileTemplate() {
    return html`
          <div class="se-main-header--main">
            <se-container>
            <div class="se-main-header--logo-search">
              <div class="se-main-header--logo">
                <slot name="logo"></slot>
              </div>
              <div class="se-main-header--search">
                <se-button
                id="search-nav-button"
                variant="none"
                aria-haspopup="true"
                aria-controls="mobile-search-panel"
                button-label="search"
                @click=${this.toggleSearch}
                ?is-disabled=${this.searchOpen ? true : false}
                >
                <outline-icon
                slot="left"
                library="system"
                name="search"   
                size="18px"
                ></outline-icon>
              </se-button>
              <se-button 
              variant="none"
              @click="${this.toggleMobileMenu}"
              id="menu-nav-button"
              aria-controls="mobile-nav-menu"
              >
              <outline-icon
                slot="left"
                library="system"
                name="menu"
                size="18px"
                ></outline-icon>
              </se-button>
            </div>
          </div>
        </se-container>
          <div class="se-main-header--search-slot closed" id="mobile-search-panel" aria-expanded="${
            this.searchOpen
          }">
              <slot name="search"></slot>
            </div>
            <div class="se-main-header--nav-utility closed" id="mobile-nav-menu" aria-expanded="${
              this.mobileMenuOpen
            }">
            <div class="se-main-header--nav-mobile">
              <se-container>
                <slot name="nav"></slot>
              </se-container>
            </div>
              <slot name="utility"></slot>
            </div>
          </div>
        </div>
    `;
  }
  connectedCallback() {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(() => {
      this.resetAtScreen();
    });
  }

  firstUpdated() {
    this.isMobile = this.mobileController.isMobile;
    this.resizeObserver.observe(this.main);
    this.addEventListener('click', this.handleOffSearchClick);
  }

  disconnectedCallback() {
    this.resizeObserver.unobserve(this.main);
    this.removeEventListener('click', this.handleOffSearchClick);
  }

  updated() {
    this.handleScrollLock();
  }

  /**
   * Closes menus/search and removes overlay when switching between desktop/mobile
   */
  resetAtScreen() {
    if (this.isMobile !== this.mobileController.isMobile) {
      this.searchOpen = false;
      this.mobileMenuOpen = false;
      this.removeOverlay();
      this.isMobile = this.mobileController.isMobile;
    }
  }

  /**
   * opens and closes menu on mobile, toggles overlay.
   */
  toggleMobileMenu() {
    this.mobileMenu.classList.toggle('closed');
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.toggleOverlay();
  }

  /**
   * @param e KeyboardEvent
   * key handler for se-dropdown in se-main-nav, toggles overlay on open/close.
   */
  handleOverlayOnKeydown(e: KeyboardEvent) {
    const header = document.querySelector('se-main-header') as SeMainHeader;
    if ((e.key !== 'Enter' && e.key !== ' ') || header.isMobile) {
      // bail
      return;
    } else {
      header.toggleOverlay();
    }
  }

  /**
   * Close Search when click anywhere else on header
   */
  handleOffSearchClick(e: Event) {
    const target = e.target as Node;

    if (
      this.searchOpen &&
      (target.nodeName === 'SE-UTILITY-NAV' ||
        target.nodeName === 'SE-MAIN-HEADER')
    ) {
      this.closeSearchPanel();
    }
  }

  handleScrollLock() {
    if (this.isMobile) {
      if (this.searchOpen || this.mobileMenuOpen) {
        return this.applyScrollLock();
      }
    }

    if (!this.isMobile) {
      if (this.searchOpen) {
        return this.applyScrollLock();
      }
    }
    return this.removeScrollLock();
  }

  /**
   * toggles overlay
   */
  toggleOverlay() {
    const header = document.querySelector('se-main-header') as SeMainHeader;
    const overlays = header.shadowRoot?.querySelectorAll('.overlay');

    if (
      header.hasOverlay &&
      !!overlays?.length &&
      !header.mobileMenuOpen &&
      !header.searchOpen
    ) {
      return header.removeOverlay();
    }

    if (!header.hasOverlay && !overlays?.length) {
      return header.applyOverlay();
    }
  }

  /**
   * creates and applies overlay div.
   */
  applyOverlay() {
    const header = document.querySelector('se-main-header') as SeMainHeader;
    const overlay = header.shadowRoot?.querySelector('.overlay');
    const isMobile = header.mobileController.isMobile;

    if (isMobile && this.localName !== 'se-main-header') {
      // bail. Main menu opne/close handles overlay on mobile.
      return;
    }

    if (!header.hasOverlay && !overlay) {
      const overlay = document.createElement('div');
      overlay.style.height = `${
        window.visualViewport.height - header.offsetHeight
      }px`;
      overlay.classList.add('overlay');
      header.shadowRoot?.append(overlay);
      overlay.setAttribute('tabindex', '0');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.addEventListener.bind(this)('focus', header.overlayFocusCapture);
      header.hasOverlay = true;
    }
  }

  /**
   * keeps focus on overlay at desktop.
   */
  overlayFocusCapture() {
    if (this.searchOpen) {
      const searchCloseButton = document
        .querySelector('se-search-panel')
        ?.shadowRoot?.querySelector('#search-panel-button')?.shadowRoot
        ?.children[0] as HTMLElement;
      searchCloseButton?.focus();
    }
    if (this.mobileMenuOpen) {
      const mobileMenuButton = this.shadowRoot?.querySelector(
        '#menu-nav-button'
      )?.shadowRoot?.children[0] as HTMLElement;
      mobileMenuButton?.focus();
    }
  }

  /**
   * removes overlay
   */
  removeOverlay() {
    const header = document.querySelector('se-main-header') as SeMainHeader;
    const overlays = header.shadowRoot?.querySelectorAll('.overlay');

    if (header.isMobile) {
      if (this.localName !== 'se-main-header') {
        // bail. Main menu open/close handles overlay on mobile.
        return;
      }
      if (header.mobileMenuOpen || header.searchOpen) {
        // prevent edgecase
        return;
      }
      overlays?.forEach(overlay => overlay.remove());
      header.hasOverlay = false;
    }

    if (!header.isMobile) {
      if (this.localName !== 'se-main-header' && header.searchOpen) {
        // prevent edgecase
        return;
      }
      if (!!overlays?.length && header.hasOverlay) {
        overlays.forEach(overlay => overlay.remove());
        header.hasOverlay = false;
      }
    }
  }

  handleEscape(e: KeyboardEvent) {
    if (e.key !== 'Escape') {
      //bail
      return;
    } else {
      if (this.searchOpen) {
        this.toggleSearch();
      }
    }
  }

  applyScrollLock() {
    const body = document.querySelector('body');
    this.main.classList.add('scroll-locked');
    body?.classList.add('body-scroll-locked');
  }

  removeScrollLock() {
    const body = document.querySelector('body');

    this.main.classList.remove('scroll-locked');
    body?.classList.remove('body-scroll-locked');
  }

  openSearchPanel() {
    const header = document.querySelector('se-main-header') as SeMainHeader;
    const searchCloseButton = document
      .querySelector('se-search-panel')
      ?.shadowRoot?.querySelector('#search-panel-button')?.shadowRoot
      ?.children[0] as HTMLElement;

    header.searchOpen = !header.searchOpen;
    header.toggleOverlay();
    header.searchPanel.classList.toggle('closed');

    if (header.mobileController.isMobile && header.mobileMenuOpen) {
      header.toggleMobileMenu();
    }

    return searchCloseButton.focus();
  }

  closeSearchPanel() {
    const header = document.querySelector('se-main-header') as SeMainHeader;
    const searchOpenButton = this.shadowRoot?.querySelector(
      '#search-nav-button'
    )?.shadowRoot?.children[0] as HTMLElement;
    header.searchOpen = !header.searchOpen;
    header.searchPanel.classList.toggle('closed');
    header.removeOverlay();
    return searchOpenButton.focus();
  }

  /**
   * hide/show search panel and toggle overlay
   */
  toggleSearch(e?: Event) {
    const header = document.querySelector('se-main-header') as SeMainHeader;
    e?.stopPropagation();
    if (!header.searchOpen) {
      return header.openSearchPanel();
    }
    if (header.searchOpen) {
      header.closeSearchPanel();
      return header.removeOverlay();
    }
  }
}
